import {ITraderApplication, ITradingFormField} from 'constants/interfaces';

import {
  lshExtractBbbeeLevelFromTraderApplication,
  lshExtractRelatedPartiesFromTraderApplication,
  lshExtractRegistrationNumberFromTraderApplication,
  lshResolveEntityTypeTitleFromTraderApplication,
} from '../../tradingFormJourneyUtils';

import {BbbeeOption, CompanyRelatedParty} from './companyDetails.types';

export const DEFAULT_BBBEE_OPTIONS = Array.from({length: 8}, (_, index) => ({
  label: `Level ${index + 1}`,
  value: String(index + 1),
}));

export const COMPANY_PROGRESS_STEP = 2;

export function lshToText(value: unknown, fallback = 'Not provided'): string {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
}

export function lshToEditableText(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

export function lshFieldId(field?: ITradingFormField): string {
  return lshToEditableText(field?.id).toLowerCase();
}

export function lshFieldTitle(field?: ITradingFormField): string {
  return lshToEditableText(field?.title).toLowerCase();
}

export function lshHasAny(value: string, tokens: string[]): boolean {
  return tokens.some((token) => value.includes(token));
}

export function lshFindFirstField(
  fields: ITradingFormField[],
  matcher: (field: ITradingFormField) => boolean,
): ITradingFormField | undefined {
  return fields.find((field) => matcher(field));
}

export function lshExtractRegistrationNumber(
  traderApplication?: ITraderApplication,
): string {
  return lshExtractRegistrationNumberFromTraderApplication(traderApplication);
}

export function lshExtractCompanyType(
  traderApplication?: ITraderApplication,
  fallback?: string,
): string {
  return lshResolveEntityTypeTitleFromTraderApplication(
    traderApplication,
    lshToEditableText(fallback),
  );
}

export function lshNormaliseBbbeeLevel(value: string): string {
  const match = value.match(/[1-8]/);
  return match ? match[0] : '';
}

export function lshResolveBbbeeValueFromOptions(
  source: string,
  options: BbbeeOption[],
): string {
  const normalized = lshToEditableText(source);
  if (!normalized) return '';

  const directMatch = options.find(
    (option) => lshToEditableText(option.value) === normalized,
  );
  if (directMatch) return lshToEditableText(directMatch.value);

  const normalizedLevel = lshNormaliseBbbeeLevel(normalized);
  if (!normalizedLevel) return normalized;

  const byLevel = options.find((option) => {
    const optionValue = lshToEditableText(option.value);
    const optionLabel = lshToEditableText(option.label);

    return (
      optionValue === normalizedLevel ||
      optionLabel.includes(`Level ${normalizedLevel}`) ||
      optionLabel.includes(`level ${normalizedLevel}`)
    );
  });

  return lshToEditableText(byLevel?.value || normalizedLevel);
}

export function lshExtractSingleChoiceOptions(
  field?: ITradingFormField,
): BbbeeOption[] {
  const options = ((field as any)?.options ||
    (field as any)?.singleChoiceOptionOptions ||
    []) as Array<{title?: string; value?: string | number}>;

  return options
    .map((option) => ({
      label:
        lshToEditableText(option?.title) || lshToEditableText(option?.value),
      value: lshToEditableText(option?.value),
    }))
    .filter((option) => option.value);
}

export function lshResolveSelectValueFromOptions(
  source: string,
  options: BbbeeOption[],
): string {
  const normalized = lshToEditableText(source);
  if (!normalized || !options.length) return normalized;

  const byValue = options.find(
    (option) => lshToEditableText(option.value) === normalized,
  );
  if (byValue) return lshToEditableText(byValue.value);

  const normalizedLower = normalized.toLowerCase();
  const byLabel = options.find(
    (option) =>
      lshToEditableText(option.label).toLowerCase() === normalizedLower,
  );
  if (byLabel) return lshToEditableText(byLabel.value);

  return normalized;
}

export function lshExtractBbbeeLevel(
  traderApplication?: ITraderApplication,
): string {
  return lshNormaliseBbbeeLevel(
    lshExtractBbbeeLevelFromTraderApplication(traderApplication),
  );
}

export function lshExtractRelatedParties(
  traderApplication?: ITraderApplication,
  fallback?: Array<{
    id: string;
    fullName: string;
    idNumber: string;
    phone: string;
    address: string;
  }>,
): CompanyRelatedParty[] {
  const traderParties = lshExtractRelatedPartiesFromTraderApplication(
    traderApplication,
  ).map((party, index) => ({
    id: lshToEditableText(party.id || index + 1),
    title: lshToText(party.fullName || `Party ${index + 1}`),
    idNumber: lshToText(party.idNumber),
    phone: lshToText(party.phone),
    address: lshToText(party.address),
    creditScore:
      typeof party.creditScore === 'number' && party.creditScore < 0
        ? 'Not available'
        : Number.isFinite(Number(party.creditScore)) &&
          Number(party.creditScore) < 0
        ? 'Not available'
        : lshToText(party.creditScore, 'Not available'),
  }));

  if (traderParties.length > 0) {
    return traderParties;
  }

  return (fallback || []).map((party) => ({
    id: party.id,
    title: lshToText(party.fullName),
    idNumber: lshToText(party.idNumber),
    phone: lshToText(party.phone),
    address: lshToText(party.address),
    creditScore: 'Not available',
  }));
}
